// @ts-nocheck -- legacy receiving contracts are migrated incrementally below.
import { useRef, useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../../../shared/usersForDropdown";
import {
  USER_DEPARTMENT_CODE,
  USER_TYPE_CODE,
} from "../../../../shared/userReferenceCodes";
import {
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
} from "../../../../shared/locationDropdowns";
import ModelErrorComponent from "../../../../components/modals/ModelErrorComponent";
import ReceivingAssemblyItemDetails from "../../../../components/forms/receiving/invoices/ReceivingAssemblyItemDetails";
import { SnackbarData, SnackbarType } from "../../../../components/Snackbar";
import { generateUniqueNumber } from "../../../../shared/utility";
import ProgressModal from "../../../../components/modals/ProgressModal";
import { buildAddItemToReceivingInvoicePayload } from "../../../../receiving/addItemToReceivingInvoicePayload";
import { normalizeReceivingInvoiceStatus } from "../../../../receiving/receivingInvoiceStatus";

function formatAddItemToReceivingInvoiceError(callResult) {
  const d = callResult?.data;
  if (typeof d?.message === "string" && d.message.trim() !== "") {
    return d.message;
  }
  if (d?.errors && typeof d.errors === "object") {
    const parts = Object.values(d.errors).flat();
    if (parts.length) return parts.join(" ");
  }
  switch (callResult?.errorStatus) {
    case 400:
      return "Barcode already exists.";
    case 503:
      return "Could not allocate barcode sequence. Try again or reduce carton count.";
    default:
      return "Receiving item could not be added.";
  }
}

export function ReceivingInvoiceDetailsViewModel(
  logout,
  invoiceId,
  addSnackbar,
) {
  const [barcodeDetails, setBarcodeDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [invoice, setInvoice] = useState({
    id: 0,
    supplier_name: "",
    invoice_number: "",
    is_barcode: { value: -1, label: "" },
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lotsLoading, setLotsLoading] = useState({});
  const [itemsLoading, setItemsLoading] = useState({});
  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);

  const getInvoiceProducts = async function () {
    let callResult = await makeGetApiCall(`invoiceProducts/${invoiceId}`);
    if (callResult.success) {
      setInvoiceItems(callResult.data.receiving_items);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getInvoiceLots = async function (
    assemblyItemId,
    zoneId,
    productUniqueId,
  ) {
    setLotsLoading({ ...lotsLoading, [productUniqueId]: true });

    const resolvedZoneId = zoneId ?? 0;
    let callResult = await makeGetApiCall(
      `invoiceLots/${invoiceId}/${assemblyItemId}/${resolvedZoneId}`,
    );

    if (callResult.success) {
      // Update specific product with its lots
      const updated = invoiceItems.map((p) =>
        p.unique_id === productUniqueId
          ? {
              ...p,
              lots: callResult.data.items_by_lot_and_item.map((lot) => ({
                ...lot,
                sub_items: null, // Not loaded yet
              })),
            }
          : p,
      );
      setInvoiceItems(updated);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    setLotsLoading({ ...lotsLoading, [productUniqueId]: false });
  };

  const getInvoiceLotItems = async function (
    productLot,
    assemblyItemId,
    productUniqueId,
  ) {
    const key = `${productUniqueId}_${productLot}`;
    setItemsLoading({ ...itemsLoading, [key]: true });

    let callResult = await makeGetApiCall(
      `invoiceLotItems/${invoiceId}/${assemblyItemId}/${encodeURIComponent(productLot)}`,
    );

    if (callResult.success) {
      // Update specific lot with its items
      const updated = invoiceItems.map((p) =>
        p.unique_id === productUniqueId
          ? {
              ...p,
              lots: p.lots.map((lot) =>
                lot.lot_number === productLot
                  ? { ...lot, sub_items: callResult.data.items }
                  : lot,
              ),
            }
          : p,
      );
      setInvoiceItems(updated);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    setItemsLoading({ ...itemsLoading, [key]: false });
  };

  const updateReceivingInvoiceStatus = async function (nextStatus) {
    const status = normalizeReceivingInvoiceStatus(nextStatus);
    if (normalizeReceivingInvoiceStatus(invoice.status) === status) {
      return;
    }

    const callResult = await makePutApiCall(
      `receivingInvoices/${invoiceId}/status`,
      { status },
    );

    if (callResult.success) {
      setInvoice((current) => ({ ...current, status }));
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Status updated.",
          SnackbarType.Success,
        ),
      );
      return;
    }

    if (callResult.errorStatus === 401) {
      logout();
      return;
    }

    showErrorModal(
      callResult.data?.message || "Could not update the receiving status.",
    );
  };

  const getReceivingInvoiceDetails = async function () {
    let callResult = await makePostApiCall(`receivingInvoices/${invoiceId}`);
    if (callResult.success) {
      const receivingInvoice = callResult.data.receiving_invoice;
      setLoadError("");
      setInvoice({
        is_barcode: { value: -1, label: "" },
        ...receivingInvoice,
      });
      return receivingInvoice;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const message =
        callResult.data?.message || "Could not load this receiving invoice.";
      setLoadError(message);
      showErrorModal(message);
    }
  };

  const addItemToReceivingInvoice = async function (values) {
    const payload = buildAddItemToReceivingInvoicePayload(values, {
      invoiceId,
    });
    let callResult = await makePostApiCall(
      "addItemToReceivingInvoice",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Receiving item added successfully",
          SnackbarType.Success,
        ),
      );
      // Reload products to get updated totals
      await getInvoiceProducts();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      showErrorModal(formatAddItemToReceivingInvoiceError(callResult));
    }
    return callResult;
  };

  const updateReceivingInvoiceApiCall = async function (values, invoiceId) {
    let callResult = await makePutApiCall(
      `receivingInvoices/${invoiceId}`,
      values,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getAllAssemblyItems = async function (supplierId) {
    console.log("getAllAssemblyItems_edit", supplierId);
    // const payload = { supplier_id: supplierId ? supplierId : null };
    let callResult = await makePostApiCall(`allAssemblyItems`, {});

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onGenerateBarcodePdf = async function (values) {
    const payload = { ...values };

    let callResult = await makePostApiCall("createBarcodePdf", payload);

    if (callResult.success) {
      setBarcodeDetails(callResult.data);
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(callResult.data.message);
        return callResult;
      }
    }
  };

  const getAssemblyItemByPlu = async function (plu) {
    const payload = { plu: plu };
    let callResult = await makePostApiCall(`assemblyItems/plu/${plu}`, payload);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return false;
    }
  };

  const printReceivingReportByID = async function () {
    showProgressModal("Generating report...");
    const payload = { filter: { receiving_id: invoiceId } };
    let callResult = await makePostApiCall(
      `/qaReports/receivingReport`,
      payload,
    );

    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal(callResult.data.message);
      return false;
    }
  };

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message={message} />,
    });
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const updateReceivingAssemblyCall = async function (values, id) {
    let callResult = await makePostApiCall(
      `updateReceivingAssemblyItem/${id}`,
      values,
    );

    if (callResult.success) {
      setSlideOver({
        childComponent: null,
        open: false,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );

      await getInvoiceProducts();

      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return false;
    }
  };

  const openReceivingItemDetails = async function (receivingItemId) {
    let callResult = await makePostApiCall(`receivingItem/${receivingItemId}`);

    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ReceivingAssemblyItemDetails
            barcodeDetails={barcodeDetails}
            setBarcodeDetails={setBarcodeDetails}
            onGenerateBarcodePdf={onGenerateBarcodePdf}
            receivingItem={{
              ...callResult.data.receiving_item,
              invoice_number: invoice.invoice_number,
            }}
            setOpen={setSlideOver}
            updateReceivingAssemblyCall={updateReceivingAssemblyCall}
          />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const deleteReceivingItem = async function (receivingItemId) {
    let callResult = await makePostApiCall(
      `deleteReceivingItem/${receivingItemId}`,
    );

    if (callResult.success) {
      // Reload products to get fresh totals from backend
      await getInvoiceProducts();

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  const deleteMultipleReceivingItems = async function (
    receivingInvoiceId,
    itemIds,
  ) {
    const payload = {
      receiving_invoice_id: receivingInvoiceId,
      items: (itemIds || []).map((id) => ({ id })),
    };

    let callResult = await makePostApiCall(
      "deleteMultipleReceivingAssemblyItems",
      payload,
    );

    if (callResult.success) {
      await getInvoiceProducts();

      const message = callResult.data.message;
      const hasUndeletableItems = callResult.data.not_deleted_items;

      if (hasUndeletableItems) {
        showReceivingItemsDeleteModal(message);
        return { ...callResult, success: false };
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            Array.isArray(message)
              ? message[0]
              : message || "Items deleted successfully.",
            SnackbarType.Success,
          ),
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  const showReceivingItemsDeleteModal = (message) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} title={message} />
      ),
      open: true,
    });
  };

  const getUsersForDropdown = async function () {
    let callResult = await fetchUsersForDropdown(
      [USER_TYPE_CODE.REGULAR, USER_TYPE_CODE.SUPERVISOR],
      [USER_DEPARTMENT_CODE.ALL, USER_DEPARTMENT_CODE.RECEIVING],
    );

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getSuppliersForDropdown = async function () {
    let callResult = await makeGetApiCall(`manufacturersForDropdown`);

    if (callResult.success) {
      setSuppliers(callResult.data.suppliers);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers.");
    }
  };

  const getPurchaseOrderForDropdown = async function () {
    let callResult = await makeGetApiCall(`purchaseOrdersForDropdown`);
    if (callResult.success) {
      setPurchaseOrderOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch purchase orders.");
    }
  };

  const getLocationsForDropdown = async function () {
    const callResult = await fetchLocationsForDropdown();
    if (callResult.success) {
      const options = callResult.data || [];
      setLocationOptions(options);
      return options;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return [];
    }
    showErrorModal(callResult.data?.message || "Could not fetch locations.");
    return [];
  };

  const init = async function () {
    setLoading(true);
    setLoadError("");
    try {
      const [, receivingInvoice] = await Promise.all([
        getUsersForDropdown(),
        getReceivingInvoiceDetails(),
        getInvoiceProducts(),
        getSuppliersForDropdown(),
        getPurchaseOrderForDropdown(),
        getLocationsForDropdown(),
      ]);
      if (receivingInvoice?.location?.value) {
        await loadZonesForLocation(receivingInvoice.location);
      }
    } catch {
      setLoadError(
        (current) => current || "Could not load this receiving invoice.",
      );
    } finally {
      setLoading(false);
    }
  };

  const zoneRequestIdRef = useRef(0);
  const zonesForLocationRef = useRef(null);
  const loadZonesForLocation = async function (location) {
    const requestId = ++zoneRequestIdRef.current;
    if (!location?.value) {
      zonesForLocationRef.current = null;
      setZoneOptions([]);
      return [];
    }
    if (zonesForLocationRef.current === location.value) {
      return [];
    }
    const callResult = await fetchLocationZonesForDropdown(location);
    if (requestId !== zoneRequestIdRef.current) {
      return [];
    }
    if (callResult.success) {
      const options = callResult.data || [];
      zonesForLocationRef.current = location.value;
      setZoneOptions(options);
      return options;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return [];
    }
    showErrorModal(callResult.data?.message || "Could not fetch zones.");
    zonesForLocationRef.current = null;
    setZoneOptions([]);
    return [];
  };

  return {
    addItemToReceivingInvoice,
    barcodeDetails,
    getAllAssemblyItems,
    getAssemblyItemByPlu,
    invoice,
    invoiceItems,
    setInvoiceItems,
    onGenerateBarcodePdf,
    loading,
    loadError,
    init,
    setSlideOver,
    slideOver,
    updateReceivingInvoiceApiCall,
    updateReceivingInvoiceStatus,
    getReceivingInvoiceDetails,
    setEmptyModelOpen,
    emptyModal,
    showErrorModal,
    openReceivingItemDetails,
    deleteReceivingItem,
    deleteMultipleReceivingItems,
    getUsersForDropdown,
    employeeOptions,
    getSuppliersForDropdown,
    suppliers,
    printReceivingReportByID,
    getInvoiceProducts,
    getInvoiceLots,
    getInvoiceLotItems,
    lotsLoading,
    itemsLoading,
    getPurchaseOrderForDropdown,
    purchaseOrderOptions,
    getLocationsForDropdown,
    locationOptions,
    zoneOptions,
    loadZonesForLocation,
  };
}
